import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { List, ArrowRight, X, Compass } from "@phosphor-icons/react";
import InviteFriends from "@/components/InviteFriends";
import ListenButton from "@/components/ListenButton";
import LanguageSelector from "@/components/LanguageSelector";
import TranslatedProse from "@/components/TranslatedProse";
import CmsPhotoRail from "@/components/CmsPhotoRail";
import ShareButton from "@/components/ShareButton";

/**
 * Manifesto — "A Living Diary of the Planet"
 *
 * A long-form editorial page with a sticky left-side chapter tab-strip
 * (scroll-spy) on desktop and a bottom-sheet "Chapters" drawer on mobile.
 * Section IDs are stable and can be linked to (e.g. /manifesto#weather).
 */

const IMG_WATERFALL = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/kirsyd6c_naturepulse-1.webp";
const IMG_WILDFLOWER = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/rmkb1uxy_naturepulse2.jpeg";
const IMG_FARMER = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/dvwazvu9_naturepulse3.jpeg";
const IMG_CLASSROOM = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/lm7h8qyl_naturepulse4.webp";
const IMG_RESTORATION = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/s3d064qi_naturepulse5.webp";
const IMG_LABURNUM = "https://customer-assets-m6fa6gv7.emergentagent.net/job_green-timeline/artifacts/tp1rr943_naturepulse6.jpeg";

const CHAPTERS = [
  {
    id: "vision",
    kicker: "01",
    title: "A living diary",
    subtitle: "The idea",
    sections: [
      {
        heading: "Documenting the Living Planet, One Day at a Time",
        eyebrow: "Introduction",
        lede: true,
        paragraphs: [
          "What if we could create a living diary of the planet?",
          "Every day, something changes. A tree blooms. A flower disappears. A new bird arrives. A wetland dries. A stream rises after heavy rain. A farmer watches the sky and changes the day's work. A child discovers a plant they have never seen before. A storm passes through a neighborhood. A familiar tree is cut down. A new species appears in a place where nobody remembers seeing it before.",
          "Most of these changes happen quietly. And most are never recorded.",
          "NaturePulse.net is built around a simple idea: what if we started documenting our planet every day? Not just through satellites, scientific instruments and government monitoring stations, but through millions of people who live on the planet and experience nature where they are.",
        ],
        stack: [
          "A photograph.",
          "A location.",
          "A date.",
          "A few words.",
          "A weather observation.",
          "A comment.",
          "A question.",
        ],
        outro: "Together, these small observations can become something extraordinary: a continuously growing record of the living world.",
      },
      {
        heading: "Every Place Has a Story",
        eyebrow: "🌎 The story",
        image: {
          src: IMG_WATERFALL,
          caption: "A photograph, a location, a date — an observation is born.",
          credit: "NaturePulse contributor",
        },
        paragraphs: [
          "Imagine someone standing in their backyard today. They take a photograph of a flowering tree.",
        ],
        meta: [
          ["📍 Location", "Their neighborhood"],
          ["📅 Date", "August 21, 2026"],
          ["🌡 Weather", "82°F, partly cloudy"],
          ["🌿 Observation", "Tree flowering earlier than usual"],
          ["📷 Photograph", "Uploaded"],
          ["💬 Comment", "I don't remember seeing these flowers this early."],
        ],
        paragraphs2: [
          "It may seem insignificant. But imagine that same thing happening 10,000 times a day, in 10,000 different places. Then imagine it happening for years.",
        ],
        pullquote: "Suddenly, we have something incredibly valuable: a visual, geographic and chronological diary of our planet.",
      },
    ],
  },
  {
    id: "weather",
    kicker: "02",
    title: "The weather we live in",
    subtitle: "Sky, rain & the day's work",
    sections: [
      {
        heading: "Document Today's Weather",
        eyebrow: "☀️ Weather",
        paragraphs: [
          "Weather isn't just a number on a phone. For millions of people, weather determines what they do today.",
          "For a farmer, rain — or the absence of it — can determine whether a crop survives. For someone living near a river, today's rainfall may provide an early indication of tomorrow's water level. For people living in low-lying coastal areas, unusually high tides, heavy rain and storms can matter enormously. For outdoor workers, travelers, fishermen and communities vulnerable to extreme weather, knowing what is happening around them can be important.",
          "Scientific weather stations provide essential measurements. But people can also document what they actually experience on the ground.",
        ],
        stack: [
          "A photograph of a flooded street.",
          "A note about an unusually strong wind.",
          "The first snowfall.",
          "The arrival of monsoon rain.",
          "An unusually dry stream.",
          "A field that has become waterlogged.",
          "A river that has risen.",
          "A neighborhood experiencing extreme heat.",
        ],
        outro: "These observations can provide valuable context around formal measurements. NaturePulse can become a place where ordinary observations become part of a larger environmental record. What is happening where you are today? Document it.",
      },
      {
        heading: "The Weather We Experience Matters",
        eyebrow: "🌧️ Ground truth",
        paragraphs: [
          "A weather station might report rainfall at one location. But conditions can vary dramatically a few miles away. One neighborhood may flood while another remains dry. One hillside may receive heavy rain while a nearby valley receives much less. One farmer may have standing water in a field while another is struggling with drought.",
          "That's why geographically documented observations matter. When an observation includes a GPS location, date and time, it becomes more than a photograph. It becomes a point in a growing environmental map.",
        ],
        pullquote: "Over time, those points can reveal patterns. And patterns can help us understand change.",
      },
    ],
  },
  {
    id: "growing",
    kicker: "03",
    title: "What's growing where",
    subtitle: "Living map · lost & found",
    sections: [
      {
        heading: "What Is Growing Where?",
        eyebrow: "🌱 The living map",
        paragraphs: [
          "Now imagine something even more powerful. What if we could build a living map of the world's vegetation? Not just forests seen from satellites. Not just agricultural statistics. But actual observations from people:",
        ],
        quotes: [
          "This tree grows here.",
          "This flower appeared here.",
          "This plant used to grow here, but I haven't seen it for five years.",
          "This species has appeared in my neighborhood for the first time.",
          "These wildflowers are blooming earlier this year.",
          "This wetland plant has disappeared.",
        ],
        outro: "Each observation can be connected to a location. Over time, NaturePulse could help build an extraordinary record of what grows where — and how that changes.",
      },
      {
        heading: "What Did We Lose?",
        eyebrow: "🌳 Memory",
        paragraphs: [
          "This may become one of the most meaningful questions NaturePulse can help us ask. What used to grow here?",
          "Many of us have photographs of places from our childhood. A tree that once stood outside a school. A forest that has become a housing development. A stream that used to run year-round. Wildflowers that no longer appear. Birds that were once common. Fruit trees that disappeared from a neighborhood. Plants that our grandparents knew but younger generations have never seen.",
          "NaturePulse could help people document those memories and observations.",
        ],
        pullquote: "What was here before? What is here now? What have we lost? And perhaps most importantly: what can we bring back?",
      },
      {
        heading: "Can We Bring Nature Back?",
        eyebrow: "🌿 Restoration",
        image: {
          src: IMG_RESTORATION,
          caption: "Two neighbours compare notes on the land they share — the seed of every restoration project is a conversation.",
          credit: "NaturePulse contributor",
        },
        paragraphs: [
          "Imagine discovering that a native plant once common in your region has disappeared from your neighborhood.",
        ],
        stack: [
          "You search NaturePulse.",
          "You discover photographs and observations from decades or years ago.",
          "You learn what the plant needs to grow.",
          "You discover that it still exists in another nearby region.",
          "You learn how it supports local insects, birds or other wildlife.",
          "Then you plant it in your garden. Your school plants it. Your community plants it.",
          "A few years later, someone photographs it again. Nature has returned.",
        ],
        outro: "This is the kind of connection we want to encourage. Not simply documenting loss — but using knowledge to inspire restoration.",
      },
    ],
  },
  {
    id: "discovery",
    kicker: "04",
    title: "Curiosity & discovery",
    subtitle: "Photos become data",
    sections: [
      {
        heading: "Discover Something New",
        eyebrow: "🐦 Curiosity",
        image: {
          src: IMG_LABURNUM,
          caption: "Someone leans in to photograph a cascade of yellow laburnum blooms. A question becomes a study.",
          credit: "NaturePulse contributor",
        },
        paragraphs: [
          "NaturePulse isn't only about scientists. It is for the curious. Especially children and students.",
          "Imagine a child walking through a park and seeing a strange little flower. They take a photograph. They upload it to NaturePulse. They record where they found it. They ask: \u201cWhat is this?\u201d",
          "They discover its name. They learn where it grows. They learn whether it is native. They learn what insects depend on it. They learn when it flowers. They learn how it reproduces. They discover that it is found in other parts of the world.",
          "A five-minute observation becomes a science lesson.",
        ],
        outro: "And perhaps that child becomes interested in biology. Or ecology. Or agriculture. Or forestry. Or climate science. Or conservation. Or simply becomes someone who notices and cares about nature.",
      },
      {
        heading: "A Photograph Can Become Data",
        eyebrow: "📸 Data",
        image: {
          src: IMG_WILDFLOWER,
          caption: "A child kneels in the grass and photographs a wildflower — a five-minute observation becomes a science lesson.",
          credit: "NaturePulse contributor",
        },
        paragraphs: [
          "At the heart of NaturePulse is something wonderfully simple: take a photograph. Photographs can document:",
        ],
        chips: [
          "🌳 Trees", "🌿 Plants", "🌺 Flowers", "🍄 Fungi", "🐦 Birds", "🦋 Butterflies",
          "🐝 Pollinators", "🐾 Animals", "🌊 Rivers", "🏞️ Landscapes", "☁️ Clouds",
          "🌧️ Rain", "❄️ Snow", "🔥 Wildfires", "🌾 Crops", "🌱 Gardens", "💧 Wetlands",
        ],
        outro: "Add: Where? When? What did you see? What did you notice? And you've created an observation.",
      },
      {
        heading: "Location Makes the Observation More Powerful",
        eyebrow: "📍 Coordinates",
        paragraphs: [
          "A photograph without a location tells us what something looked like. A photograph with a location tells us where. Add the date and time, and we know when. Add a short description, and we know what someone observed.",
          "Now imagine millions of these observations. We can begin asking:",
        ],
        quotes: [
          "Where is this species growing?",
          "Where has it disappeared?",
          "When does it flower?",
          "How is its range changing?",
          "Where are invasive plants appearing?",
          "Where are forests recovering?",
          "Where are wetlands shrinking?",
          "Where are new species being observed?",
          "Where are environmental conditions changing?",
        ],
        outro: "The individual photograph becomes part of a much larger picture.",
      },
    ],
  },
  {
    id: "diary",
    kicker: "05",
    title: "A daily diary",
    subtitle: "One place, ten years",
    sections: [
      {
        heading: "The Planet Has a Daily Diary",
        eyebrow: "🗓️ Ritual",
        paragraphs: [
          "We photograph our vacations. We photograph our families. We photograph our food. We photograph important events.",
          "Why not photograph the place where we live? Take one photograph every day.",
        ],
        stack: [
          "The same tree. The same garden. The same river.",
          "The same coastline. The same field. The same mountain. The same street.",
        ],
        pullquote: "After a year, you have a story. After ten years, you have a record. After generations, you have history.",
        outro: "NaturePulse can help create a daily visual diary of our planet.",
      },
    ],
  },
  {
    id: "voices",
    kicker: "06",
    title: "Voices that matter",
    subtitle: "Farmers · scientists · classrooms",
    sections: [
      {
        heading: "A Farmer's Observation Can Matter",
        eyebrow: "👩‍🌾 Field knowledge",
        image: {
          src: IMG_FARMER,
          caption: "A farmer stands in his potato field, noting today's conditions on his phone. Traditional knowledge, quietly digitised.",
          credit: "NaturePulse contributor",
        },
        paragraphs: [
          "Farmers have always been observers of nature. They know when the rains arrive. They know when crops flower. They know when pests appear. They know when the soil becomes too dry. They know when birds return. They know when temperatures feel unusual.",
          "Much of this knowledge is never formally recorded. NaturePulse can give farmers a simple way to document it.",
        ],
        stack: [
          "A photograph of a crop.",
          "A weather observation.",
          "A pest sighting.",
          "A flowering date.",
          "A rainfall observation.",
          "A note about soil conditions.",
        ],
        outro: "Over time, these observations can help create a richer picture of agricultural and environmental conditions. Traditional knowledge and modern technology can complement each other.",
      },
      {
        heading: "A Scientist's Observation Can Matter",
        eyebrow: "👨‍🔬 Discovery layer",
        paragraphs: [
          "Scientists need data. Long-term environmental records are especially valuable.",
          "NaturePulse is not intended to replace scientific instruments or professional research. Instead, it can complement them by creating a large network of human observations and photographs.",
          "A scientist may eventually be able to explore observations by:",
        ],
        chips: ["Location", "Date", "Species", "Weather", "Season", "Elevation", "Habitat", "Environmental condition"],
        outro: "The platform could become a discovery layer connecting people with scientific inquiry.",
      },
      {
        heading: "A Classroom Without Walls",
        eyebrow: "🏫 Learning",
        image: {
          src: IMG_CLASSROOM,
          caption: "Two students stoop over a patch of milkweed and ferns, notebooks open. The planet becomes the classroom.",
          credit: "NaturePulse contributor",
        },
        paragraphs: [
          "Imagine a school where students are asked: \u201cFind five different plants growing within one kilometer of your school.\u201d",
          "They photograph them. Record their locations. Identify them. Research their origins. Learn which insects interact with them. Then compare their findings with students in another city.",
          "A classroom in Kerala could compare observations with students in California. A student in Africa could compare rainfall observations with a student in India. A child in a city could discover the biodiversity hidden around their own neighborhood.",
        ],
        pullquote: "Suddenly, the planet becomes the classroom.",
      },
    ],
  },
  {
    id: "connection",
    kicker: "07",
    title: "Connection & care",
    subtitle: "Witnesses · problems",
    sections: [
      {
        heading: "Learn About Nature. Love Nature.",
        eyebrow: "❤️ Care",
        paragraphs: [
          "There is an important connection between knowledge and care. It is difficult to love something we never notice. And it is difficult to protect something we don't understand.",
          "When a child learns the name of a tree, that tree becomes more than \u201ca tree.\u201d It becomes something known. When someone discovers the bird that visits their garden every morning, they begin watching for it. When a family learns about a wetland near their home, they may begin to appreciate why it matters. When people understand where their food comes from, they may think differently about soil and water.",
        ],
        ladder: [
          ["Observation", "creates curiosity."],
          ["Curiosity", "creates knowledge."],
          ["Knowledge", "can create appreciation."],
          ["Appreciation", "can create responsibility."],
          ["Responsibility", "can lead to action."],
        ],
      },
      {
        heading: "We Are All Witnesses",
        eyebrow: "🌍 Witnesses",
        paragraphs: [
          "The planet does not belong only to scientists. It belongs to all of us — and to generations that haven't been born yet.",
          "Every person can become a witness.",
        ],
        chips: [
          "A farmer", "A student", "A teacher", "A photographer", "A gardener", "A traveler",
          "A parent", "A fisherman", "A hiker", "A scientist", "A child", "A grandparent",
        ],
        outro: "You don't need expensive equipment. Sometimes all you need is a phone, a few minutes and curiosity.",
      },
      {
        heading: "Document Problems, Too",
        eyebrow: "🧹 Honesty",
        paragraphs: [
          "NaturePulse should not only show beautiful nature. We should also document what is hurting it. Photograph:",
        ],
        chips: [
          "Plastic pollution", "Illegal dumping", "Polluted streams", "Dead fish",
          "Deforestation", "Erosion", "Destroyed wetlands", "Invasive plants",
          "Smoke", "Litter", "Damaged habitats",
        ],
        outro: "But these observations should be documented responsibly and, where possible, connected with reliable information and appropriate authorities or organizations. We cannot solve problems we refuse to see.",
      },
    ],
  },
  {
    id: "future",
    kicker: "08",
    title: "From here forward",
    subtitle: "Restoration · 2036 · invitation",
    sections: [
      {
        heading: "From Documentation to Restoration",
        eyebrow: "🌱 Purpose",
        paragraphs: [
          "The ultimate purpose isn't to create the world's largest collection of photographs. It is to create understanding and action.",
          "A NaturePulse observation might eventually lead to:",
        ],
        stack: [
          "A student project.",
          "A scientific investigation.",
          "A community cleanup.",
          "A native garden.",
          "A restoration project.",
          "A farmer changing a practice.",
          "A school planting trees.",
          "A neighborhood protecting a wetland.",
          "A family learning about biodiversity.",
          "A community rediscovering a lost plant.",
        ],
        outro: "That's when documentation becomes meaningful.",
      },
      {
        heading: "Imagine NaturePulse 10 Years From Now",
        eyebrow: "🔭 2036",
        paragraphs: [
          "Imagine opening NaturePulse in 2036. You select your hometown. You see today's environment. Then you move the timeline backward.",
          "You can see photographs of the same places. The same trees. The same rivers. The same fields. The same neighborhoods. You can see what changed.",
        ],
        stack: [
          "Some things disappeared.",
          "Some things returned.",
          "Some things flourished.",
          "Some new species arrived.",
          "Some old species came back.",
        ],
        pullquote: "And because people documented it, the history wasn't lost.",
      },
      {
        heading: "One Observation at a Time",
        eyebrow: "🌎 Together",
        paragraphs: [
          "No single person can document the entire planet. But millions of people can document their little piece of it.",
        ],
        chips: [
          "Your backyard", "Your farm", "Your school", "Your neighborhood",
          "Your forest", "Your river", "Your coastline", "Your community",
          "Your daily weather", "Your tree", "Your birds", "Your flowers", "Your observations",
        ],
        outro: "Put them together, and something remarkable happens. We begin to see the planet as a living, changing system rather than a static photograph.",
      },
      {
        heading: "NaturePulse Is an Invitation",
        eyebrow: "🌿 Invitation",
        paragraphs: [
          "We invite you to become an observer. Look outside.",
        ],
        quotes: [
          "What is growing?",
          "What is flowering?",
          "What is flying?",
          "What is changing?",
          "What has disappeared?",
          "What has returned?",
          "What is the weather doing today?",
          "What did you see that you've never seen before?",
        ],
        outro: "Take a photograph. Add the location. Add the date. Write a few words. Ask a question. Share what you discovered. And come back tomorrow. Because tomorrow the planet will be slightly different. And someone should be there to document it.",
      },
    ],
  },
];

const flatSectionIds = CHAPTERS.flatMap((c) => c.sections.map((_, i) => `${c.id}-${i}`));

// ---------- Section renderer ----------
function SectionBlock({ chapterId, index, section, dropCap }) {
  return (
    <section
      id={`${chapterId}-${index}`}
      data-testid={`section-${chapterId}-${index}`}
      className="scroll-mt-28 md:scroll-mt-32 mb-16 md:mb-24"
    >
      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
        / {section.eyebrow}
      </div>
      <h2 className="font-serif-display text-3xl md:text-5xl leading-[1.05] tracking-tight text-[#151312] mb-6">
        {section.heading}
      </h2>

      {section.image && (
        <figure className="my-6" data-listen-skip>
          <div className="rounded-2xl overflow-hidden border border-black/10 bg-black/5 shadow-[0_20px_50px_-20px_rgba(0,0,0,0.35)]">
            <img
              src={section.image.src}
              alt={section.image.caption}
              loading="lazy"
              className="w-full h-auto block object-cover"
              data-testid={`section-image-${chapterId}-${index}`}
            />
          </div>
          <figcaption className="mt-3 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] flex flex-wrap items-baseline justify-between gap-2">
            <span className="max-w-[80%] normal-case tracking-normal font-serif-display text-sm text-[#3A3830] italic">
              {section.image.caption}
            </span>
            {section.image.credit && <span>— {section.image.credit}</span>}
          </figcaption>
        </figure>
      )}

      {section.paragraphs?.map((p, i) => (
        <p
          key={`p-${i}`}
          className={`font-serif-display text-lg md:text-xl leading-[1.7] text-[#241209] mb-5 ${
            dropCap && i === 0 ? "first-letter:font-serif-display first-letter:text-[68px] first-letter:leading-[0.85] first-letter:mr-2 first-letter:float-left first-letter:text-[color:var(--np-brand,#B85F2A)]" : ""
          }`}
        >
          {p}
        </p>
      ))}

      {section.stack && (
        <ul className="my-6 space-y-2 border-l-2 border-[color:var(--np-brand,#B85F2A)]/50 pl-5">
          {section.stack.map((s, i) => (
            <li key={i} className="font-serif-display text-lg md:text-xl leading-snug text-[#241209]">
              {s}
            </li>
          ))}
        </ul>
      )}

      {section.meta && (
        <div className="my-6 rounded-2xl border border-black/10 bg-white/70 backdrop-blur p-5 md:p-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
          {section.meta.map(([k, v], i) => (
            <div key={i} className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3">
              <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] whitespace-nowrap">
                {k}
              </div>
              <div className="font-serif-display text-base md:text-lg text-[#151312]">{v}</div>
            </div>
          ))}
        </div>
      )}

      {section.paragraphs2?.map((p, i) => (
        <p key={`p2-${i}`} className="font-serif-display text-lg md:text-xl leading-[1.7] text-[#241209] mb-5">
          {p}
        </p>
      ))}

      {section.quotes && (
        <div className="my-6 space-y-2">
          {section.quotes.map((q, i) => (
            <div
              key={i}
              className="font-serif-display text-lg md:text-xl italic text-[#241209] pl-5 border-l-2 border-[#151312]/20"
            >
              &ldquo;{q}&rdquo;
            </div>
          ))}
        </div>
      )}

      {section.chips && (
        <ul className="flex flex-wrap gap-2 my-6">
          {section.chips.map((c, i) => (
            <li
              key={i}
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 border border-black/10 bg-white/60 backdrop-blur font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#241209]"
            >
              {c}
            </li>
          ))}
        </ul>
      )}

      {section.ladder && (
        <div className="my-6 rounded-2xl bg-gradient-to-br from-white to-[#FBF2E1] border border-black/10 p-5 md:p-6">
          <ol className="space-y-3">
            {section.ladder.map(([k, v], i) => (
              <li key={i} className="flex items-baseline gap-4">
                <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)] w-4">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-serif-display text-lg md:text-xl text-[#151312]">
                  <b className="font-serif-display">{k}</b> {v}
                </span>
              </li>
            ))}
          </ol>
        </div>
      )}

      {section.pullquote && (
        <blockquote className="my-8 md:my-10 border-y-2 border-[color:var(--np-brand,#B85F2A)]/40 py-6">
          <div className="font-serif-display text-2xl md:text-3xl leading-[1.25] tracking-tight text-[#151312]">
            &ldquo;{section.pullquote}&rdquo;
          </div>
        </blockquote>
      )}

      {section.outro && (
        <p className="font-serif-display text-lg md:text-xl leading-[1.7] text-[#241209]">
          {section.outro}
        </p>
      )}
    </section>
  );
}

// ---------- Main page ----------
export default function Manifesto() {
  const [active, setActive] = useState(flatSectionIds[0]);
  const [progress, setProgress] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const observed = useRef(new Map());

  // Scroll-spy via IntersectionObserver — highlight the section closest to 30% viewport.
  useEffect(() => {
    const els = flatSectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => observed.current.set(e.target.id, e));
        const visible = Array.from(observed.current.values())
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 },
    );
    els.forEach((el) => observer.observe(el));

    const onScroll = () => {
      const h = document.documentElement;
      const total = h.scrollHeight - h.clientHeight;
      setProgress(total > 0 ? Math.min(1, Math.max(0, h.scrollTop / total)) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { observer.disconnect(); window.removeEventListener("scroll", onScroll); };
  }, []);

  const activeChapterId = active.split("-").slice(0, -1).join("-");

  const jumpTo = (id) => {
    setDrawerOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="bg-[#F5EBDC] text-[#151312] min-h-screen pt-32 md:pt-36 pb-32" data-testid="manifesto-page">
      {/* Reading progress bar */}
      <div
        aria-hidden
        className="fixed top-[64px] md:top-[70px] left-0 h-[2px] bg-[color:var(--np-brand,#B85F2A)] z-40"
        style={{ width: `${progress * 100}%` }}
      />

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-4">
          / Manifesto · A NaturePulse essay
        </div>
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[0.92] tracking-tighter max-w-4xl">
          NaturePulse<span className="text-[color:var(--np-brand,#B85F2A)]">.</span>net
          <span className="block mt-3 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-[#3A3830]">
            Documenting the Living Planet, One Day at a Time
          </span>
        </h1>
        <p className="mt-6 font-serif-display text-lg md:text-xl italic text-[#3A3830] max-w-3xl">
          Observe. Document. Learn. Share. Protect. A living diary of our planet — created by all of us, one observation at a time.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3" data-listen-skip>
          <Link
            to="/pulse"
            data-testid="manifesto-cta-pulse"
            className="inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:opacity-90"
          >
            <Compass size={14} weight="fill" /> Open My Pulse
          </Link>
          <InviteFriends variant="chip" label="Invite someone who cares" />
        </div>

        {/* Article tools — Listen (TTS) + Language (Translate) */}
        <div
          className="mt-6 flex flex-wrap items-center gap-2 rounded-2xl border border-black/10 bg-white/70 backdrop-blur px-4 py-3"
          data-listen-skip
          data-testid="article-tools"
        >
          <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mr-1">
            / Tools
          </span>
          <ListenButton label="Listen to article" />
          <LanguageSelector variant="light" />
          <ShareButton
            title="NaturePulse.net — Documenting the Living Planet, One Day at a Time"
            text="A manifesto essay from NaturePulse."
            hashtags={["NaturePulse", "Nature", "ClimateAction"]}
            variant="chip"
            testid="manifesto-share"
          />
          <span className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[color:var(--np-muted,#3A3830)] ml-1">
            20+ languages · read aloud
          </span>
        </div>
      </section>

      {/* Body */}
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 mt-14 md:mt-20 grid grid-cols-12 gap-6 md:gap-10">
        {/* Sticky side-tab TOC (desktop) */}
        <aside className="hidden lg:block col-span-3 xl:col-span-3">
          <nav className="sticky top-28" aria-label="Chapters">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-muted,#3A3830)] mb-3">
              / Chapters
            </div>
            <ol className="space-y-1">
              {CHAPTERS.map((c) => {
                const isActive = activeChapterId === c.id;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => jumpTo(`${c.id}-0`)}
                      data-testid={`toc-${c.id}`}
                      className={`w-full text-left group flex items-start gap-3 px-3 py-2.5 rounded-lg border transition-colors ${
                        isActive
                          ? "bg-[#151312] text-[#F5EBDC] border-[#151312]"
                          : "bg-transparent text-[#241209] border-transparent hover:bg-white/60 hover:border-black/10"
                      }`}
                    >
                      <span className={`font-mono-tech text-[10px] tracking-[0.28em] mt-0.5 ${isActive ? "text-[#E4A15C]" : "text-[color:var(--np-brand,#B85F2A)]"}`}>
                        {c.kicker}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-serif-display text-base leading-tight">{c.title}</span>
                        <span className={`block font-mono-tech text-[9px] tracking-[0.22em] uppercase mt-1 ${isActive ? "text-[#E5D8C4]/75" : "text-[color:var(--np-muted,#3A3830)]"}`}>
                          {c.subtitle}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            <div className="mt-6 pt-6 border-t border-black/10">
              <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">
                Reading progress
              </div>
              <div className="h-1 rounded-full bg-black/10 overflow-hidden">
                <div
                  className="h-full bg-[color:var(--np-brand,#B85F2A)]"
                  style={{ width: `${Math.round(progress * 100)}%` }}
                />
              </div>
              <div className="mt-1 font-mono-tech text-[9px] tracking-[0.22em] text-[color:var(--np-muted,#3A3830)]">
                {Math.round(progress * 100)}%
              </div>
            </div>
          </nav>
        </aside>

        {/* Article */}
        <article className="col-span-12 lg:col-span-9 xl:col-span-8 max-w-[720px]" data-listen-root>
          <TranslatedProse cacheKey="manifesto-v1">
          {CHAPTERS.map((c, ci) => (
            <div key={c.id} className="mb-4">
              <div className="mb-8 md:mb-10 flex items-baseline gap-4 border-b border-black/10 pb-3">
                <div className="font-mono-tech text-[11px] tracking-[0.32em] text-[color:var(--np-brand,#B85F2A)]">
                  {c.kicker}
                </div>
                <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-muted,#3A3830)]">
                  {c.subtitle}
                </div>
              </div>
              {c.sections.map((s, i) => (
                <SectionBlock
                  key={`${c.id}-${i}`}
                  chapterId={c.id}
                  index={i}
                  section={s}
                  dropCap={ci === 0 && i === 0}
                />
              ))}
            </div>
          ))}

          {/* Community photo library — pulls from the CMS. Silent when empty. */}
          <CmsPhotoRail
            eyebrow="From the community lens"
            title="Photos uploaded to the NaturePulse library"
            limit={12}
          />

          {/* Sign-off */}
          <div className="mt-6 rounded-2xl bg-[#151312] text-[#F5EBDC] p-6 md:p-8">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#E4A15C] mb-3">
              / NaturePulse.net
            </div>
            <div className="font-serif-display text-2xl md:text-3xl leading-[1.15] tracking-tight">
              Observe. Document. Learn. Share. Protect.
            </div>
            <p className="mt-3 font-serif-display text-base md:text-lg text-[#E5D8C4]/80">
              Take a photograph. Mark the place. Tell the story. Learn what lives around you. Understand what is changing. Help future generations know the world we inherited — and the world we leave behind.
            </p>
            <p className="mt-4 font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[#E4A15C]">
              Know your nature. Love your nature. Protect your future.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/pulse"
                data-testid="manifesto-footer-pulse"
                className="inline-flex items-center gap-2 rounded-full bg-[#F5EBDC] text-[#151312] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:opacity-90"
              >
                Open My Pulse <ArrowRight size={14} weight="bold" />
              </Link>
              <Link
                to="/challenges"
                data-testid="manifesto-footer-challenges"
                className="inline-flex items-center gap-2 rounded-full border border-[#E4A15C]/60 text-[#E4A15C] px-5 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase hover:bg-[#E4A15C] hover:text-[#151312]"
              >
                Start a 7-day challenge <ArrowRight size={14} weight="bold" />
              </Link>
              <InviteFriends variant="chip" label="Invite someone" />
            </div>
          </div>
          </TranslatedProse>
        </article>
      </div>

      {/* Mobile floating "Chapters" button */}
      <button
        onClick={() => setDrawerOpen(true)}
        data-testid="manifesto-toc-toggle"
        className="lg:hidden fixed bottom-24 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-[#151312] text-[#F5EBDC] px-4 py-2.5 font-mono-tech text-[10px] tracking-[0.3em] uppercase shadow-xl"
      >
        <List size={14} weight="bold" /> Chapters
      </button>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setDrawerOpen(false)}
            data-testid="manifesto-toc-backdrop"
          />
          <motion.div
            className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-[#F5EBDC] p-5 pt-4 max-h-[75dvh] overflow-y-auto"
            initial={{ y: 400 }} animate={{ y: 0 }} exit={{ y: 400 }}
            transition={{ type: "spring", stiffness: 260, damping: 28 }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                / Chapters
              </div>
              <button onClick={() => setDrawerOpen(false)} aria-label="Close">
                <X size={16} weight="bold" />
              </button>
            </div>
            <ol className="space-y-1">
              {CHAPTERS.map((c) => {
                const isActive = activeChapterId === c.id;
                return (
                  <li key={c.id}>
                    <button
                      onClick={() => jumpTo(`${c.id}-0`)}
                      data-testid={`toc-mobile-${c.id}`}
                      className={`w-full text-left flex items-start gap-3 px-3 py-3 rounded-lg border ${
                        isActive
                          ? "bg-[#151312] text-[#F5EBDC] border-[#151312]"
                          : "bg-white/70 text-[#241209] border-black/10"
                      }`}
                    >
                      <span className={`font-mono-tech text-[10px] tracking-[0.28em] mt-0.5 ${isActive ? "text-[#E4A15C]" : "text-[color:var(--np-brand,#B85F2A)]"}`}>
                        {c.kicker}
                      </span>
                      <span className="min-w-0">
                        <span className="block font-serif-display text-base leading-tight">{c.title}</span>
                        <span className={`block font-mono-tech text-[9px] tracking-[0.22em] uppercase mt-0.5 ${isActive ? "text-[#E5D8C4]/75" : "text-[color:var(--np-muted,#3A3830)]"}`}>
                          {c.subtitle}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </motion.div>
        </div>
      )}
    </main>
  );
}
